// The visual vocabulary of a workflow row: one solid chip per action, one flat
// chip for the trigger, a switch, and a small icon button.
//
// Solid fills, no tints and no gradients. At row density a pastel chip and its
// neighbour read as the same colour, and the whole point of colouring a step is
// that you can tell "WhatsApp" from "email" without reading either word.

import React from "react";
import { MessageCircle, Mail, FileText, Phone, CalendarCheck, CheckSquare, Clock, Flag, Zap } from "lucide-react";
import { ACTION_COLORS, TRIGGER_COLOR } from "../../../theme/palette";
import { actionOf, describeTrigger } from "./workflowRules.mjs";

// `wait` and `stop` are timeline marks rather than steps — they only ever show
// up in history, never in a rule, so they carry the muted end of the palette.
export const ACTION_UI = {
  whatsapp: { icon: MessageCircle, color: ACTION_COLORS.whatsapp },
  email: { icon: Mail, color: ACTION_COLORS.email },
  pdf: { icon: FileText, color: ACTION_COLORS.pdf },
  call: { icon: Phone, color: ACTION_COLORS.call },
  meeting: { icon: CalendarCheck, color: ACTION_COLORS.meeting },
  task: { icon: CheckSquare, color: ACTION_COLORS.task },
  wait: { icon: Clock, color: ACTION_COLORS.wait },
  stop: { icon: Flag, color: ACTION_COLORS.task },
};

export const uiOf = (id) => ACTION_UI[id] || ACTION_UI.email;

export function ActionChip({ action, label, title }) {
  const { icon: Icon, color } = uiOf(action);
  return (
    <span
      title={title}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0,
        height: 22, padding: "0 8px", borderRadius: 5,
        background: color, color: "#fff",
        fontSize: 11.5, fontWeight: 500, whiteSpace: "nowrap",
      }}
    >
      <Icon size={11} strokeWidth={2.2} />
      {label ?? actionOf(action).label}
    </span>
  );
}

export function TriggerChip({ trigger }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0,
      height: 22, padding: "0 8px", borderRadius: 5,
      background: TRIGGER_COLOR, color: "#fff",
      fontSize: 11.5, fontWeight: 500, whiteSpace: "nowrap",
    }}>
      <Zap size={11} strokeWidth={2.2} />
      {describeTrigger(trigger)}
    </span>
  );
}

// The gap between two chips carries the wait: a chevron when the next step goes
// out with the previous one, a day count when it doesn't.
export function StepGap({ waitDays }) {
  const d = Number(waitDays) || 0;
  if (!d) {
    return <span aria-hidden="true" style={{ flexShrink: 0, color: "var(--color-text-muted)", fontSize: 11 }}>›</span>;
  }
  return (
    <span style={{
      flexShrink: 0, fontSize: 10.5, fontWeight: 500, padding: "0 5px",
      height: 17, lineHeight: "17px", borderRadius: 4,
      background: "var(--color-bg-muted)", color: "var(--color-text-secondary)",
      fontVariantNumeric: "tabular-nums",
    }}>
      +{d}d
    </span>
  );
}

// State is never colour-only: the word next to the switch says which way it is.
export function Toggle({ on, onChange, label, id }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={(e) => { e.stopPropagation(); onChange(!on); }}
        style={{
          width: 32, height: 18, borderRadius: 99, border: "none", padding: 2,
          background: on ? ACTION_COLORS.whatsapp : "var(--color-border-strong)",
          cursor: "pointer", display: "inline-flex", justifyContent: on ? "flex-end" : "flex-start",
          alignItems: "center", transition: "background-color 140ms ease",
        }}
      >
        <span style={{ width: 14, height: 14, borderRadius: 99, background: "#fff", display: "block" }} />
      </button>
      <span style={{
        fontSize: 11.5, fontWeight: 500, width: 24,
        color: on ? "var(--color-text-primary)" : "var(--color-text-muted)",
      }}>
        {on ? "On" : "Off"}
      </span>
    </span>
  );
}

export const iconBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 26, height: 26, border: "1px solid var(--color-border-subtle)",
  background: "var(--color-bg-surface)", borderRadius: 6, cursor: "pointer",
  color: "var(--color-text-secondary)", flexShrink: 0,
};
